/**
 * Structural promises the expansion's demolition, sabotage and endurance missions share (10 §1), as asserts:
 * every "X" of the fiction is a flagged target, a rule the player can lose to has all its words, a blackout
 * group switches real lamps and real machines, what belongs in water stands in it, men who arrive later
 * really are waiting.
 */

import { barrierAt } from '../../sim/hazards/terrain';
import { collidersFor, PROPS } from '../../sim/props';
import type { MissionDef, ObjectiveRule, PropDef, PropKind, Rect, SpawnDef } from '../../sim/types';
import { targetsOf } from './helpers';

export type Check = (ok: boolean, message: string) => void;

type TimedRule = Extract<ObjectiveRule, { type: 'timedEvent' }>;

/** Every prop of these kinds is a target and flagged for the strip map, and nothing else is (10 §1.4). */
export function checkTargets(check: Check, def: MissionDef, kinds: readonly PropKind[]): PropDef[] {
  const targets = targetsOf(def);
  const all = def.props.filter((p) => kinds.includes(p.kind));
  check(targets.length > 0, 'the mission has no targets');
  check(targets.length === def.objective.targetPropIds.length, 'a target id names no prop');
  check(targets.every((t) => kinds.includes(t.kind)), `every target must be one of: ${kinds.join(', ')}`);
  check(all.every((p) => targets.includes(p)), `every ${kinds.join(' / ')} on the map must count as a target`);
  check(targets.every((t) => t.target === true), 'every target must be flagged for the strip map');
  return targets;
}

const said = (text: string | undefined): boolean => text !== undefined && text.trim().length > 0;

/**
 * No invisible rules (10 §1.1): every timed event and every alert consequence that matters to the player has
 * its standing order and its countdown words; one that can lose the mission has its reason too.
 */
export function checkRuleText(check: Check, def: MissionDef): void {
  def.objective.rules.forEach((rule, i) => {
    if (rule.type === 'alertConsequence' && rule.effect !== 'reinforceTarget') {
      check(
        said(rule.orders) && said(rule.warning) && said(rule.reason),
        `rule ${i} (${rule.effect}) needs orders, warning and reason`,
      );
    }
    // Being seen has no countdown: its order and its reason are all it has to say.
    if (rule.type === 'notSeenNear') check(said(rule.orders) && said(rule.reason), `rule ${i} (notSeenNear) needs orders and reason`);
    if (rule.type !== 'timedEvent') return;
    // The Osprey that comes the moment the hold is complete never shows a countdown: its order is enough.
    const instant = rule.from === 'complete' && rule.atSeconds === 0;
    check(
      said(rule.orders) && (instant || said(rule.warning)),
      `rule ${i} (${rule.effect}) needs its orders and warning`,
    );
    const losing = rule.effect === 'targetsLeave' || rule.effect === 'strikeLands' || rule.failIfIncomplete === true;
    if (losing) check(said(rule.reason), `rule ${i} (${rule.effect}) can lose the mission and needs a reason`);
    check(
      rule.atSeconds >= 0 && rule.atSeconds === Math.round(rule.atSeconds),
      `rule ${i}: whole seconds, not ${rule.atSeconds}`,
    );
  });
}

export const timedRules = (def: MissionDef, effect?: TimedRule['effect']): TimedRule[] =>
  def.objective.rules.filter(
    (r): r is TimedRule => r.type === 'timedEvent' && (effect === undefined || r.effect === effect),
  );

/** Men who are not on the map until the event tagged `tag` fires. */
export const waiting = (def: MissionDef, tag: string): SpawnDef[] =>
  def.spawns.filter((s) => s.dormantUntil === 'timedEvent' && s.wakeTag === tag);

/** A blackout group must throw real switches: its switch a prop, its lamps searchlights, its guns automated spawns. */
export function checkBlackouts(check: Check, def: MissionDef, switches: readonly PropDef[]): void {
  const groups = def.blackoutGroups ?? [];
  check(groups.length === switches.length, `needs ${switches.length} blackout group(s), has ${groups.length}`);
  const lamps = new Set(def.props.filter((p) => p.kind === 'searchlight').map((p) => p.id));
  const wired = new Set<number>();
  const powered = new Set<number>();
  groups.forEach((group, i) => {
    check(
      switches.some((s) => s.id === group.switchPropId),
      `blackout group ${i} hangs on prop ${group.switchPropId}, no switch`,
    );
    check(group.searchlightPropIds.length + group.spawnIndexes.length > 0, `blackout group ${i} switches nothing off`);
    for (const id of group.searchlightPropIds) {
      check(lamps.has(id), `blackout group ${i} lists prop ${id}, which is no searchlight`);
      check(!wired.has(id), `searchlight ${id} hangs on two switches`);
      wired.add(id);
    }
    for (const index of group.spawnIndexes) {
      const kind = def.spawns[index]?.kind;
      check(
        kind === 'miniBunker' || kind === 'minitank',
        `blackout group ${i} lists spawn ${index}, which is no machine`,
      );
      check(!powered.has(index), `spawn ${index} hangs on two switches`);
      powered.add(index);
    }
  });
  check(wired.size === lamps.size, `${lamps.size - wired.size} searchlight(s) hang on no switch`);
}

/** Does this point lie in a water barrier's bed (open water or ice, ford or not)? */
export const inWater = (def: MissionDef, x: number, z: number): boolean => barrierAt(def, x, z) !== null;

/** A `wet` prop must stand in the water it belongs to - all four corners of it. */
export function checkAfloat(check: Check, def: MissionDef, prop: PropDef, inset = 0.4): void {
  check(PROPS[prop.kind].wet === true, `${prop.kind} ${prop.id} does not belong in water`);
  for (const c of collidersFor(prop)) {
    const ex = Math.max(0, (c.shape === 'circle' ? c.r : c.hx) - inset);
    const ez = Math.max(0, (c.shape === 'circle' ? c.r : c.hz) - inset);
    const corners = [[-ex, -ez], [ex, -ez], [-ex, ez], [ex, ez]];
    const wet = corners.every(([dx, dz]) => inWater(def, c.x + (dx ?? 0), c.z + (dz ?? 0)));
    check(wet, `${prop.kind} ${prop.id} is not wholly in the water`);
  }
}

export const insideMap = (def: MissionDef, r: Rect): boolean =>
  r.x0 >= 0 && r.z0 >= 0 && r.x1 <= def.sizeX && r.z1 <= def.sizeZ;

/** Nearest-point distance from a blast at a prop's centre to another prop's footprint (damage.ts `explode`). */
export function blastGap(from: PropDef, to: PropDef): number {
  let best = Infinity;
  for (const c of collidersFor(to)) {
    const nx = c.shape === 'circle' ? c.x : Math.min(c.x + c.hx, Math.max(c.x - c.hx, from.x));
    const nz = c.shape === 'circle' ? c.z : Math.min(c.z + c.hz, Math.max(c.z - c.hz, from.z));
    const toCentre = Math.sqrt((nx - from.x) * (nx - from.x) + (nz - from.z) * (nz - from.z));
    const d = toCentre - (c.shape === 'circle' ? c.r : 0);
    best = Math.min(best, d);
  }
  return best;
}

/**
 * Would `from` cooking off take `to` with it? Cook-offs are heavy blasts: 0.7 of the radius on soft things, 0.55 on
 * hard.
 */
export function cooksOff(from: PropDef, to: PropDef): boolean {
  const blast = PROPS[from.kind].secondaryBlast;
  const durability = PROPS[to.kind].durability;
  if (blast === undefined || durability === 'none' || durability === 'bombOnly') return false;
  return blastGap(from, to) <= blast * (durability === 'hard' ? 0.55 : 0.7);
}
